import { cn } from '@/utilities/ui'
import { useCallback, useState, type JSX, type MouseEventHandler } from 'react'
import { IconChevronLeft, IconChevronRight, IconList, IconRotateClockwise } from '@tabler/icons-react'
import Link from 'next/link'

import { OutlineButton } from './OutlineButton'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ChildBlock, FlatTitle } from '../FlatTitle'
import { Locale } from '@/providers/Locale/types'
import { i18n } from './i18n'

interface Props {
    locale: Locale
    headings: Array<ChildBlock | string>
    postUrl: string
    numberedList?: boolean
    slideNumber: number
    totalSlides: number
    onNavigationClick: MouseEventHandler<HTMLButtonElement>
}

export function SlideshowHeader({ locale, headings, postUrl, slideNumber, totalSlides, onNavigationClick, numberedList = true }: Props): JSX.Element {
    const [openList, setOpenList] = useState(false)
    const openListDialog = useCallback(() => {
        setOpenList(true)
    }, [setOpenList])

    const closeListDialog = useCallback(() => {
        setOpenList(false)
    }, [setOpenList])

    return (
        <>
            <nav className={cn('justify-between mb-4 hidden', { 'lg:flex': slideNumber > 1 })}>
                <div className='flex gap-2 items-center'>
                    <div className={cn('text-lg', { hidden: slideNumber > totalSlides })}>
                        {slideNumber} / {totalSlides + 1}
                    </div>
                    <OutlineButton
                        onClick={onNavigationClick}
                        data-direction='prev'
                        className={cn('hover:border hover:border-black hover:text-black', { hidden: slideNumber > totalSlides })}
                    >
                        <IconChevronLeft />
                    </OutlineButton>
                    <OutlineButton onClick={onNavigationClick} data-direction='next' className={cn({ hidden: slideNumber > totalSlides })}>
                        <IconChevronRight />
                    </OutlineButton>
                    <OutlineButton onClick={openListDialog} className={cn({ 'lg:hidden': slideNumber <= totalSlides })}>
                        <IconList className='mr-2' /> {i18n[locale].listSlides}
                    </OutlineButton>
                    <div className='border-b-2 border-[#E5E5E5] font-bold text-lg pb-1'>
                        <FlatTitle heading={headings[slideNumber - 1]} />
                    </div>
                </div>
                <div className='flex gap-2'>
                    <OutlineButton onClick={openListDialog} className={cn({ hidden: slideNumber > totalSlides })}>
                        <IconList className='mr-2' /> {i18n[locale].listSlides}
                    </OutlineButton>
                    <OutlineButton
                        onClick={onNavigationClick}
                        data-direction='prev'
                        className={cn('hover:border hover:border-black hover:text-black', { hidden: slideNumber <= totalSlides })}
                    >
                        <IconChevronLeft /> {i18n[locale].previous}
                    </OutlineButton>
                    <OutlineButton
                        className={cn('bg-accent text-white hover:bg-accent hover:brightness-90', { hidden: slideNumber <= totalSlides })}
                        onClick={onNavigationClick}
                        data-direction='restart'
                    >
                        {i18n[locale].restart}
                        <IconRotateClockwise className='ml-2' />
                    </OutlineButton>
                </div>
            </nav>
            <Dialog open={openList} onOpenChange={setOpenList}>
                <DialogContent className='max-w-full h-full dark:bg-neutral-900'>
                    <div className='container px-8 mx-auto'>
                        <DialogTitle className='text-center mb-6 text-3xl'>All Slides</DialogTitle>
                        <ol className='md:[column-count:2] lg:[column-count:3] [counter-reset:section] text-2xl list-outside'>
                            {headings.map((heading, index) => (
                                <li
                                    key={index}
                                    className={cn('inline-block pl-10 mb-8 relative', {
                                        'before:content-[counter(section)_"."] before:[counter-increment:section_1] before:mr-4 before:absolute before:left-0':
                                            numberedList,
                                    })}
                                >
                                    <Link href={index === 0 ? postUrl : `${postUrl}/slides/${index + 1}`} className='hover:underline' onClick={closeListDialog}>
                                        <FlatTitle heading={heading} />
                                    </Link>
                                </li>
                            ))}
                        </ol>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
